import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { FileText } from "lucide-react";
import { DashboardLayout } from "@/pages/Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import FilterDropdown from "@/components/FilterDropDowns";
import type { ColumnHeader } from "@/components/MyTable";
import { PatientInfoCard } from "../components/PatientInfoCard";
import {
  getLegacyDateRangeParams,
  getLegacyMonthFilters,
  LEGACY_DEFAULT_HIDDEN_COLUMN_IDS,
  LEGACY_MONTH_FILTER_OPTIONS,
} from "../legacyTableDefaults";

const treatmentPlanHeaders: ColumnHeader[] = [
  { id: "sourcePatientId", label: "ID", isSortable: false },
  { id: "patientName", label: "Patient Name", isSortable: false },
  { id: "dob", label: "DOB", isSortable: false },
  { id: "icdCodes", label: "ICD Codes", isSortable: false },
  { id: "primaryInsurance", label: "Primary Insurance", isSortable: false },
  { id: "insuranceId", label: "Insurance ID", isSortable: false },
  { id: "address", label: "Address", isSortable: false },
  { id: "addedBy", label: "Added By", isSortable: false },
  { id: "provider", label: "Provider", isSortable: false },
  { id: "program", label: "Program", isSortable: false },
  { id: "dateOfCreation", label: "Date of Creation", isSortable: false },
];

function getFirstValue(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

function formatListValue(value: unknown) {
  const formatted = Array.isArray(value) ? value.join(", ") : value || "";
  return String(formatted).replace(/~/g, ", ");
}

const EMPTY_FILTERS = {};
const NOOP = () => {};

function mapTreatmentPlanRows(rows: any[]) {
  return rows.map((plan: any) => ({
    ...plan,
    sourcePatientId: getFirstValue(plan, ["sourcePatientId", "patientId", "patientID"]),
    patientName: getFirstValue(plan, ["patientName", "name"]),
    dob: getFirstValue(plan, ["dob", "dateOfBirth"]),
    icdCodes: formatListValue(getFirstValue(plan, ["icdCodes", "icdCode"])),
    primaryInsurance: getFirstValue(plan, [
      "primaryInsurance",
      "primaryInsuranceName",
    ]),
    insuranceId: getFirstValue(plan, [
      "insuranceId",
      "insuranceID",
      "primaryInsuranceNumber",
    ]),
    address:
      getFirstValue(plan, ["address"]) ||
      [plan?.street, plan?.city, plan?.state, plan?.zipCode]
        .filter(Boolean)
        .join(", "),
    addedBy: getFirstValue(plan, ["addedBy", "createdByName", "createdBy"]),
    provider: getFirstValue(plan, ["provider", "providerName"]),
    dateOfCreation: getFirstValue(plan, ["dateOfCreation", "createdAt"]),
  }));
}

export default function LegacyTreatmentPlan() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState<Record<string, string>>(() => getLegacyMonthFilters());
  const requestParams = useMemo(
    () => ({
      patientId,
      ...getLegacyDateRangeParams(filters.month),
    }),
    [filters.month, patientId],
  );
  const viewData = useMemo(() => mapTreatmentPlanRows(rows), [rows]);

  return (
    <DashboardLayout activePage="/legacy">
      <div className="space-y-4">
        <PatientInfoCard />
        <TableWithSearch
          heading="Past Medical Records Treatment Plans"
          url="/legacy-treatment-plans"
          params={requestParams}
          apiDataKey="treatmentPlans"
          fetchCondition={!!patientId}
          universalLoadingSkeleton
          isIncludeSearch={false}
          searchPlaceholder="Search treatment plans"
          headerExtraControls={
            <FilterDropdown
              filters={filters}
              setFilters={setFilters}
              filtersOptions={LEGACY_MONTH_FILTER_OPTIONS}
              defaultFilters={getLegacyMonthFilters()}
              isHideClearAllFilters
            />
          }
          columnHeaders={treatmentPlanHeaders}
          data={rows}
          viewData={viewData}
          setData={setRows}
          isActionColumn={false}
          filtersOptions={[]}
          handleAction={NOOP}
          filters={EMPTY_FILTERS}
          setFilters={NOOP}
          clickRow={NOOP}
          moreActions={[
            {
              label: "View",
              icon: <FileText size={16} />,
              isShow: (row: any) => Array.isArray(row?.questions) && row.questions.length > 0,
              onClick: (row: any) =>
                navigate(`/legacy/treatment-plan/${patientId}/view`, {
                  state: {
                    legacyPlan: row,
                    legacyPlanType: "treatment-plan",
                    legacyPatient: row,
                  },
                }),
            },
          ]}
          isColumnSelection
          columnPreferenceKey="legacy-treatment-plans-v5"
          defaultHiddenColumnIds={LEGACY_DEFAULT_HIDDEN_COLUMN_IDS}
          isIgnoreSearchParams
        />
      </div>
    </DashboardLayout>
  );
}
